import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/store/authStore';
import { getTrainerConfig } from '@shared/config/trainerTypes';

export const TrainerLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Проверка что пользователь - тренер
  if (!user || user.role !== 'trainer') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Доступ запрещен</h2>
          <p className="text-gray-600 mb-6">Этот раздел доступен только для тренеров</p>
          <button
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="px-6 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
          >
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  const trainer = user;

  // Получаем конфигурацию для типа тренера
  const trainerConfig = getTrainerConfig(trainer?.specName || 'Тренер');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Навигационные элементы
  const navItems = [
    {
      path: '/trainer/dashboard',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      label: 'Главная',
      name: 'dashboard'
    },
    {
      path: '/trainer/schedule',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      label: 'Записи',
      name: 'schedule'
    },
    {
      path: '/trainer/profile',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      label: 'Профиль',
      name: 'profile'
    }
  ];

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-amber-500/20 shadow-lg shadow-amber-500/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Spacer for centering */}
            <div className="flex-1"></div>

            {/* Centered Logo */}
            <div className="flex items-center justify-center">
              <div className="h-10 sm:h-14 px-3 sm:px-4 bg-slate-800/50 backdrop-blur-sm rounded-xl flex items-center justify-center border border-amber-500/30 shadow-lg shadow-amber-500/20">
                <img
                  src="/logotype-white.png"
                  alt="Level Sport Club"
                  className="h-full w-auto object-contain"
                />
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1 flex items-center justify-end space-x-2 sm:space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-white">
                  {trainer?.firstName} {trainer?.lastName}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-amber-400/70 hover:text-amber-400 hover:bg-slate-800/50 rounded-lg transition-all duration-200"
                title="Выйти"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-24">
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-lg border-t border-amber-500/20 shadow-2xl shadow-amber-500/10 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex justify-around items-center h-16 sm:h-18">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex flex-col items-center justify-center flex-1 h-full transition-all duration-200 ${
                    active
                      ? 'text-amber-400'
                      : 'text-slate-500 hover:text-amber-400/70'
                  }`}
                >
                  <div className={`${active ? 'scale-110' : ''} transition-transform duration-200`}>
                    {item.icon}
                  </div>
                  <span className="text-[10px] sm:text-xs mt-1 font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
};
